import { GROUP_TOLERANCE_DAYS } from './constants';
import { type Day, isObserved, mean } from './day';
import {
  canJoin,
  crossesBarrier,
  type EvidenceRun,
  joinNearbyRuns,
  membershipInterval,
  possibleInterval,
  sliceRun,
} from './run';

/** A campaign run for a single EAN at a single vendor channel. */
export type CampaignRun = {
  readonly ean: string;
  readonly vendorKey: string;
  readonly startDate: string; // YYYY-MM-DD
  readonly endDate: string; // YYYY-MM-DD
  readonly observedStartDate: string; // first discount day, YYYY-MM-DD
  readonly observedEndDate: string; // last discount day, YYYY-MM-DD
  readonly durationDays: number;
  readonly discountDepth: number; // decimal, e.g. 0.25 = 25%
  readonly regularPrice: number; // list price (pre-discount)
  readonly avgPriceDuring: number; // avg selling price during campaign
  readonly discountDayCount: number;
  readonly unobservedDayCount: number; // days in the run whose kind is neither discount nor regular
};

type Series = { readonly days: readonly Day[]; readonly runs: readonly EvidenceRun[] };

type SeriesByEan = ReadonlyMap<string, Series>;

// A series whose runs carry the cuts of decision 18; a fallback join never crosses a barrier day.
type Working = Series & { readonly barriers: ReadonlySet<number> };

type WorkingByEan = Map<string, Working>;

type Interval = { readonly from: number; readonly to: number };

// An evidence run in the pool with its bounds (spec decision 11).
type Candidate = {
  readonly run: EvidenceRun;
  readonly membership: Interval;
};

// One SKU's member runs joined into one run and dated against the wave.
type Snapped = {
  readonly parts: readonly Candidate[];
  readonly run: EvidenceRun;
  readonly start: number;
  readonly end: number;
};

type Resolved = { readonly members: readonly Snapped[]; readonly rest: readonly Candidate[] };

// A cluster's runs between two boundary days, dated within `limit`.
type Segment = { readonly cluster: readonly Candidate[]; readonly limit: Interval };

// A wave is dated only on days at least this many of its SKUs were observed (spec decision 10).
const MIN_OBSERVED_SKUS = 3;

function contains(outer: Interval, run: EvidenceRun): boolean {
  return outer.from <= run.first && run.last <= outer.to;
}

function seriesOf<S extends Series>(series: ReadonlyMap<string, S>, ean: string): S {
  const found = series.get(ean);
  if (!found) throw new Error(`alignRuns: no series for ${ean}`);
  return found;
}

function daysOf(series: SeriesByEan, ean: string): readonly Day[] {
  return seriesOf(series, ean).days;
}

function compareCandidates(a: Candidate, b: Candidate): number {
  return (
    b.run.discountDays.length - a.run.discountDays.length ||
    a.run.first - b.run.first ||
    a.run.ean.localeCompare(b.run.ean)
  );
}

function joinsAnchor(anchor: Candidate, c: Candidate): boolean {
  return contains(c.membership, anchor.run) && contains(anchor.membership, c.run);
}

// Anchored, non-transitive: a run joins the first cluster whose anchor it fits (spec, `wave.ts` step a).
function clusterPool(pool: readonly Candidate[]): Candidate[][] {
  const clusters: Candidate[][] = [];
  for (const c of [...pool].sort(compareCandidates)) {
    const target = clusters.find((cluster) => joinsAnchor(cluster[0], c));
    if (target) target.push(c);
    else clusters.push([c]);
  }
  return clusters;
}

// Most frequent value, ties to the smallest.
function modal(values: readonly number[]): number {
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best = values[0];
  let bestCount = 0;
  for (const [v, count] of counts) {
    if (count > bestCount || (count === bestCount && v < best)) {
      best = v;
      bestCount = count;
    }
  }
  return best;
}

type Tally = { readonly observed: number; readonly discount: number; readonly regular: number };

function tally(series: SeriesByEan, eans: readonly string[], i: number): Tally {
  let observed = 0;
  let discount = 0;
  let regular = 0;
  for (const ean of eans) {
    const day = daysOf(series, ean)[i];
    if (!isObserved(day)) continue;
    observed++;
    if (day.kind === 'discount') discount++;
    else regular++;
  }
  return { observed, discount, regular };
}

function eansOf(cluster: readonly Candidate[]): string[] {
  return [...new Set(cluster.map((c) => c.run.ean))];
}

function hullOf(cluster: readonly Candidate[]): Interval {
  return {
    from: Math.min(...cluster.map((c) => c.run.first)),
    to: Math.max(...cluster.map((c) => c.run.last)),
  };
}

// Half crossing over observed SKUs within the cluster's evidence and `limit`, else modal observed dates (spec decision 10).
function dateCluster(
  series: SeriesByEan,
  cluster: readonly Candidate[],
  limit: Interval
): Interval {
  const eans = eansOf(cluster);
  const hull = hullOf(cluster);
  const clamp = (i: number) => Math.min(limit.to, Math.max(limit.from, i));
  let from = -1;
  let to = -1;
  for (let i = clamp(hull.from); i <= clamp(hull.to); i++) {
    const { observed, discount } = tally(series, eans, i);
    if (observed < MIN_OBSERVED_SKUS || discount * 2 < observed) continue;
    if (from < 0) from = i;
    to = i;
  }
  if (from >= 0) return { from, to };
  const start = clamp(modal(cluster.map((c) => c.run.first)));
  return { from: start, to: clamp(Math.max(start, modal(cluster.map((c) => c.run.last)))) };
}

// Hull days on which at least MIN_OBSERVED_SKUS of the cluster's SKUs were observed and more than half at the regular price (spec decision 18).
function boundariesOf(series: SeriesByEan, cluster: readonly Candidate[]): number[] {
  const eans = eansOf(cluster);
  const hull = hullOf(cluster);
  const boundaries: number[] = [];
  for (let i = hull.from; i <= hull.to; i++) {
    const { observed, regular } = tally(series, eans, i);
    if (observed >= MIN_OBSERVED_SKUS && regular * 2 > observed) boundaries.push(i);
  }
  return boundaries;
}

// Maximal stretches of the hull free of boundary days.
function stretchesOf(hull: Interval, boundaries: readonly number[]): Interval[] {
  const stretches: Interval[] = [];
  let from = hull.from;
  for (const b of [...boundaries, hull.to + 1]) {
    if (b > from) stretches.push({ from, to: b - 1 });
    from = b + 1;
  }
  return stretches;
}

// The run cut at every boundary day inside it the SKU was not itself discounted on (spec decision 18).
function cutRun(
  days: readonly Day[],
  run: EvidenceRun,
  boundaries: readonly number[]
): EvidenceRun[] {
  const cuts = boundaries.filter(
    (b) => b > run.first && b < run.last && days[b].kind !== 'discount'
  );
  if (cuts.length === 0) return [run];
  const edges = [run.first - 1, ...cuts, run.last + 1];
  const pieces: EvidenceRun[] = [];
  for (let k = 0; k + 1 < edges.length; k++) {
    const piece = sliceRun(days, run, edges[k] + 1, edges[k + 1] - 1);
    if (piece) pieces.push(piece);
  }
  return pieces;
}

// Splits a cluster at its boundary days into one cluster per stretch, cutting runs and recording the boundaries as barriers (spec decision 18).
function splitCluster(series: WorkingByEan, cluster: readonly Candidate[]): Segment[] {
  const hull = hullOf(cluster);
  const boundaries = boundariesOf(series, cluster);
  const stretches = stretchesOf(hull, boundaries);
  if (boundaries.length === 0 || stretches.length === 0) return [{ cluster, limit: hull }];
  for (const ean of eansOf(cluster)) {
    const working = seriesOf(series, ean);
    series.set(ean, { ...working, barriers: new Set([...working.barriers, ...boundaries]) });
  }
  const segments = stretches.map((): Candidate[] => []);
  for (const c of cluster) {
    const working = seriesOf(series, c.run.ean);
    const pieces = cutRun(working.days, c.run, boundaries);
    if (pieces.length > 1) {
      const runs = working.runs.flatMap((r) => (r === c.run ? pieces : [r]));
      series.set(c.run.ean, { ...working, runs });
    }
    for (const run of pieces) {
      // A run that spans a boundary sits in the stretch holding or following its first day; the fit check returns it to the pool.
      const k = stretches.findIndex((s) => s.to >= run.first);
      const piece =
        pieces.length > 1 ? { run, membership: membershipInterval(working.days, run) } : c;
      segments[k < 0 ? stretches.length - 1 : k].push(piece);
    }
  }
  return stretches
    .map((limit, k) => ({ cluster: segments[k].sort(compareCandidates), limit }))
    .filter((segment) => segment.cluster.length > 0);
}

function mergeRuns(days: readonly Day[], parts: readonly Candidate[]): EvidenceRun {
  if (parts.length === 1) return parts[0].run;
  const discountDays = parts.flatMap((p) => p.run.discountDays);
  const at = (value: (d: Day) => number | null) =>
    mean(discountDays.map((i) => value(days[i]) ?? 0)) ?? 0;
  return {
    ...parts[0].run,
    first: discountDays[0],
    last: discountDays[discountDays.length - 1],
    discountDays,
    depth: at((d) => d.depth),
    regularPrice: at((d) => d.regularPrice),
  };
}

// Maximal chains of consecutive runs that canJoin and no barrier separates, by first discount day.
function chainsOf(series: Working, runs: readonly Candidate[]): Candidate[][] {
  const chains: Candidate[][] = [];
  for (const c of [...runs].sort((a, b) => a.run.first - b.run.first)) {
    const chain = chains[chains.length - 1];
    const last = chain?.[chain.length - 1].run;
    if (last && canJoin(series.days, last, c.run) && !crossesBarrier(series.barriers, last, c.run))
      chain.push(c);
    else chains.push([c]);
  }
  return chains;
}

function discountDayCount(chain: readonly Candidate[]): number {
  return chain.reduce((n, c) => n + c.run.discountDays.length, 0);
}

// The snap bound, also stopped short of the SKU's runs outside the chain so its runs never overlap.
function snapBound(series: Series, parts: readonly Candidate[], run: EvidenceRun): Interval {
  let { from, to } = possibleInterval(series.days, run);
  for (const other of series.runs) {
    if (parts.some((p) => p.run === other)) continue;
    if (other.last < run.first) from = Math.max(from, other.last + 1);
    if (other.first > run.last) to = Math.min(to, other.first - 1);
  }
  return { from, to };
}

// Wave dates where the run's snap bound allows them and they do not cut its evidence, else its observed dates (spec decision 11).
function snap(series: Series, parts: readonly Candidate[], wave: Interval): Snapped {
  const run = mergeRuns(series.days, parts);
  const bound = snapBound(series, parts, run);
  const start = wave.from >= bound.from && wave.from <= run.first ? wave.from : run.first;
  const end = wave.to <= bound.to && wave.to >= run.last ? wave.to : run.last;
  return { parts, run, start, end };
}

function byEan(candidates: readonly Candidate[]): Map<string, Candidate[]> {
  const grouped = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const list = grouped.get(c.run.ean) ?? [];
    list.push(c);
    grouped.set(c.run.ean, list);
  }
  return grouped;
}

// One SKU's best-evidenced chain snapped to the wave, or null; every other run goes back to the pool.
function memberOf(
  series: Working,
  runs: readonly Candidate[],
  wave: Interval,
  rest: Candidate[]
): Snapped | null {
  const chains = chainsOf(series, runs);
  let best = chains[0];
  for (const chain of chains) if (discountDayCount(chain) > discountDayCount(best)) best = chain;
  for (const chain of chains) if (chain !== best) rest.push(...chain);
  const snapped = snap(series, best, wave);
  // A wave's members share its dates (spec decision 9); the aggregate util's tolerance regroups the rest.
  if (snapped.start === wave.from && snapped.end === wave.to) return snapped;
  rest.push(...best);
  return null;
}

// Steps b to d of the spec's `wave.ts`: date the cluster, keep the runs in range, snap one chain per SKU.
function resolveCluster(series: WorkingByEan, { cluster, limit }: Segment): Resolved {
  const wave = dateCluster(series, cluster, limit);
  const rest: Candidate[] = [];
  const inRange: Candidate[] = [];
  for (const c of cluster) {
    const fits =
      c.run.first >= wave.from - GROUP_TOLERANCE_DAYS &&
      c.run.last <= wave.to + GROUP_TOLERANCE_DAYS;
    (fits ? inRange : rest).push(c);
  }
  const members: Snapped[] = [];
  for (const [ean, runs] of byEan(inRange)) {
    const member = memberOf(seriesOf(series, ean), runs, wave, rest);
    if (member) members.push(member);
  }
  if (members.length > 0) return { members, rest };
  // Nothing fits: the anchor alone is a wave on its observed dates, so every round emits a run.
  const [anchor] = cluster;
  const run = anchor.run;
  return {
    members: [{ parts: [anchor], run, start: run.first, end: run.last }],
    rest: cluster.slice(1),
  };
}

function campaignRun(
  days: readonly Day[],
  run: EvidenceRun,
  start: number,
  end: number
): CampaignRun {
  const prices: number[] = [];
  const regularPrices: number[] = [];
  let unobservedDayCount = 0;
  for (let i = start; i <= end; i++) {
    const day = days[i];
    if (!isObserved(day)) unobservedDayCount++;
    if (day.price !== null && day.regularPrice !== null) {
      prices.push(day.price);
      regularPrices.push(day.regularPrice);
    }
  }
  return {
    ean: run.ean,
    vendorKey: run.vendorKey,
    startDate: days[start].date,
    endDate: days[end].date,
    observedStartDate: days[run.first].date,
    observedEndDate: days[run.last].date,
    durationDays: end - start + 1,
    discountDepth: run.depth,
    regularPrice: mean(regularPrices) ?? 0,
    avgPriceDuring: mean(prices) ?? 0,
    discountDayCount: run.discountDays.length,
    unobservedDayCount,
  };
}

function candidatesOf(series: SeriesByEan): Candidate[] {
  const pool: Candidate[] = [];
  for (const { days, runs } of series.values()) {
    for (const run of runs) pool.push({ run, membership: membershipInterval(days, run) });
  }
  return pool;
}

// Stretches of `subset` adjacent in the series' run order, so a claimed run between two unclaimed ones is a barrier.
function consecutiveGroups(
  all: readonly EvidenceRun[],
  subset: readonly EvidenceRun[]
): EvidenceRun[][] {
  const groups: EvidenceRun[][] = [];
  let previous = -2;
  for (const index of subset.map((r) => all.indexOf(r)).sort((a, b) => a - b)) {
    if (index === previous + 1) groups[groups.length - 1].push(all[index]);
    else groups.push([all[index]]);
    previous = index;
  }
  return groups;
}

/** Campaign runs at one vendor channel, siblings dated and joined as waves (spec decisions 9 to 12). */
export function alignRuns(seriesByEan: SeriesByEan): CampaignRun[] {
  const aligned: CampaignRun[] = [];
  const unclaimed = new Map<string, EvidenceRun[]>();
  const series: WorkingByEan = new Map(
    [...seriesByEan].map(([ean, s]) => [ean, { ...s, barriers: new Set<number>() }])
  );
  let pool = candidatesOf(seriesByEan);
  while (pool.length > 0) {
    const next: Candidate[] = [];
    for (const segment of clusterPool(pool).flatMap((cluster) => splitCluster(series, cluster))) {
      const { members, rest } = resolveCluster(series, segment);
      next.push(...rest);
      if (members.length >= 2) {
        for (const m of members)
          aligned.push(campaignRun(daysOf(series, m.run.ean), m.run, m.start, m.end));
      } else {
        for (const m of members) {
          const list = unclaimed.get(m.run.ean) ?? [];
          list.push(...m.parts.map((p) => p.run));
          unclaimed.set(m.run.ean, list);
        }
      }
    }
    pool = next;
  }
  // A SKU no multi-SKU wave claims falls back to its own gap rule (spec decision 12).
  for (const [ean, runs] of unclaimed) {
    const { days, runs: all, barriers } = seriesOf(series, ean);
    for (const group of consecutiveGroups(all, runs)) {
      for (const run of joinNearbyRuns(days, group, barriers))
        aligned.push(campaignRun(days, run, run.first, run.last));
    }
  }
  const order = new Map([...seriesByEan.keys()].map((ean, i) => [ean, i]));
  return aligned.sort(
    (a, b) =>
      (order.get(a.ean) ?? 0) - (order.get(b.ean) ?? 0) || a.startDate.localeCompare(b.startDate)
  );
}
